import type { ObjectType, SpotDetail } from "@divespots/contracts";
import { useQuery } from "@tanstack/react-query";
import "leaflet/dist/leaflet.css";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { api } from "../api/client.ts";
import { ObjectIcon } from "../components/ObjectIcon.tsx";
import { SpotPin } from "../components/Pin.tsx";
import { formatMetres } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { L } from "../map/leaflet-global.ts";
import { BASE_LAYERS, PICKER_MAX_ZOOM, nativeZoomFor, wmtsUrl } from "../map/tiles.ts";
import type { BaseLayer } from "../map/tiles.ts";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import { useThreeDCount } from "../spot/three/three-lock.ts";
import { parseLatLon } from "./forms.ts";
import { Field } from "./ui.tsx";

const ThreeDView = lazy(() => import("../spot/three/ThreeDView.tsx"));

type LL = { lat: number; lon: number };

const LAYER_KEY = "divespots.pickerLayer";

/** The viewer's last base layer in the pickers: Luftbild unless they chose Karte. */
export function readPickerLayer(): BaseLayer {
  try {
    return localStorage.getItem(LAYER_KEY) === "map" ? "map" : "aerial";
  } catch {
    return "aerial";
  }
}
function savePickerLayer(l: BaseLayer) {
  try {
    localStorage.setItem(LAYER_KEY, l);
  } catch {
    /* no storage: the choice lasts until the page closes */
  }
}

interface Props {
  /** Group label for screen readers ("Einstiegspunkt"). */
  label: string;
  lat: string;
  lon: string;
  onChange: (lat: string, lon: string) => void;
  /** Where to centre the map while there is no valid value yet. */
  fallbackCenter: LL;
  /** An object pin (lake square) instead of the spot pin. */
  objectType?: ObjectType;
  /** Show the computed depth under the map. */
  showDepth?: boolean;
  /** Extra markers for context (e.g. the spot's entry while placing an object). */
  contextPoint?: LL | undefined;
  errors?: { lat?: string | undefined; lon?: string | undefined };
  /** Offers a 2D/3D switch (3D = the lakebed of this spot) when given. */
  threeD?: { spot: SpotDetail };
}

const fmt = (v: number) => v.toFixed(6);

function pinIcon(objectType?: ObjectType) {
  const html = renderToStaticMarkup(
    objectType ? (
      <div className="ds-pin ds-pin--object">
        <ObjectIcon type={objectType} size={16} />
      </div>
    ) : (
      <SpotPin />
    ),
  );
  return L.divIcon({ className: "spot-marker", html, iconSize: [44, 44], iconAnchor: [22, 22] });
}

function ClickToPlace({ onPick }: { onPick: (ll: LL) => void }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lon: e.latlng.lng }) });
  return null;
}

/** Leaflet only re-measures on window resize: also when the box itself changes (rotation, layout). */
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function Follow({ point }: { point: LL | null }) {
  const map = useMap();
  useEffect(() => {
    if (point && !map.getBounds().contains([point.lat, point.lon])) {
      map.panTo([point.lat, point.lon], { animate: false });
    }
  }, [map, point]);
  return null;
}

/** Leaflet map with a draggable pin (and click-to-place) plus lat/lon inputs. The map is the one glass-free exception: tiles are the content. */
export function LocationPicker({
  label,
  lat,
  lon,
  onChange,
  fallbackCenter,
  objectType,
  showDepth = true,
  contextPoint,
  errors,
  threeD,
}: Props) {
  const { t } = useTranslation();
  const point = parseLatLon(lat, lon);
  const icon = useMemo(() => pinIcon(objectType), [objectType]);
  const center = point ?? fallbackCenter;
  const [layer, setLayerState] = useState<BaseLayer>(readPickerLayer);
  const setLayer = (l: BaseLayer) => {
    setLayerState(l);
    savePickerLayer(l);
  };
  const base = BASE_LAYERS[layer];
  const [view, setView] = useState<"2d" | "3d">("2d");
  const threeCount = useThreeDCount();
  const noBathy = threeD ? !threeD.spot.hasBathy : false;
  // another 3D view (the spot sheet's) already owns the one WebGL canvas
  const busy3d = view !== "3d" && threeCount > 0;

  const place = (ll: LL) => onChange(fmt(ll.lat), fmt(ll.lon));
  const onPick = onChange;

  return (
    <div className="grid gap-3" role="group" aria-label={label}>
      <div className="adm-picker__bar">
        {threeD ? (
          <>
            <SegmentedControl
              aria-label={t("spot.view")}
              value={view}
              onChange={setView}
              options={[
                { value: "2d", label: t("spot.view2d") },
                {
                  value: "3d",
                  label: t("spot.view3d"),
                  disabled: noBathy || busy3d,
                  describedBy: noBathy ? "picker-nobathy" : undefined,
                },
              ]}
            />
            {noBathy ? (
              <span id="picker-nobathy" className="ds-text-footnote text-label-secondary">
                {t("spot.noBathy")}
              </span>
            ) : null}
          </>
        ) : null}
        {view === "2d" ? (
          <SegmentedControl
            aria-label={t("map.layerLabel")}
            value={layer}
            onChange={setLayer}
            options={[
              { value: "map", label: t("map.layerMap") },
              { value: "aerial", label: t("map.layerAerial") },
            ]}
          />
        ) : null}
      </div>
      {view === "3d" && threeD ? (
        <div className="adm-3d">
          <Suspense fallback={<p className="adm-3d__loading">{t("three.loading")}</p>}>
            <ThreeDView spot={threeD.spot} embedded={{ lat, lon, onPick }} reservedLeft={0} />
          </Suspense>
        </div>
      ) : (
        <div className="adm-map">
          <MapContainer
            center={[center.lat, center.lon]}
            zoom={16}
            minZoom={7}
            maxZoom={PICKER_MAX_ZOOM}
            zoomControl
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              key={layer}
              url={wmtsUrl(base)}
              maxZoom={PICKER_MAX_ZOOM}
              maxNativeZoom={nativeZoomFor(layer, "picker")}
            />
            <InvalidateOnResize />
            <ClickToPlace onPick={place} />
            <Follow point={point} />
            {contextPoint ? (
              <Marker
                position={[contextPoint.lat, contextPoint.lon]}
                icon={pinIcon()}
                interactive={false}
                keyboard={false}
              />
            ) : null}
            {point ? (
              <Marker
                position={[point.lat, point.lon]}
                icon={icon}
                draggable
                keyboard={false}
                eventHandlers={{
                  dragend: (e) => {
                    const ll = (e.target as L.Marker).getLatLng();
                    place({ lat: ll.lat, lon: ll.lng });
                  },
                }}
              />
            ) : null}
          </MapContainer>
          <p className="adm-map__credit ds-text-caption-1">{t("map.attribution")}</p>
        </div>
      )}
      <p className="ds-text-footnote text-label-secondary m-0">{t("admin.picker.help")}</p>
      <div className="adm-period__dates">
        <Field label={t("admin.picker.lat")} error={errors?.lat}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input ds-text-coordinate"
              inputMode="decimal"
              value={lat}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => onChange(e.target.value, lon)}
            />
          )}
        </Field>
        <Field label={t("admin.picker.lon")} error={errors?.lon}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input ds-text-coordinate"
              inputMode="decimal"
              value={lon}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => onChange(lat, e.target.value)}
            />
          )}
        </Field>
      </div>
      {showDepth ? <DepthHint point={point} /> : null}
    </div>
  );
}

/** Debounced `/api/depth` lookup for a picked point. Nodata is never shown as 0 m. */
export function DepthHint({ point }: { point: LL | null }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [debounced, setDebounced] = useState(point);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(point), 350);
    return () => clearTimeout(id);
  }, [point?.lat, point?.lon]); // eslint-disable-line react-hooks/exhaustive-deps
  const q = useQuery({
    queryKey: ["depth", debounced?.lat.toFixed(6), debounced?.lon.toFixed(6)],
    queryFn: ({ signal }) => api.getDepth((debounced as LL).lat, (debounced as LL).lon, signal),
    enabled: debounced !== null,
    retry: false,
  });
  let text: string;
  if (!point) text = t("admin.depth.pick");
  else if (q.isPending || debounced === null) text = t("common.loading");
  else if (q.isError) text = t("admin.depth.error");
  else {
    const d = q.data;
    if (d.status === "ok") text = t("admin.depth.ok", { depth: formatMetres(d.depthM, locale, 1) });
    else if (d.status === "dry") text = t("admin.depth.dry");
    else if (d.status === "no_data") text = t("admin.depth.noData");
    else text = t("admin.depth.noLake");
  }
  return (
    <p className="adm-depth ds-text-callout m-0" role="status" aria-live="polite">
      {text}
    </p>
  );
}
